import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { ArrowLeft, TrendingUp, RefreshCw, Clock } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PollTrackerPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [pollData, setPollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [summary, setSummary] = useState("");
  const [activeTab, setActiveTab] = useState("current");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const gridColor = isDark ? "#334155" : "#E5E7EB";

  useEffect(() => {
    axios
      .get(`${API}/polls`)
      .then((r) => setPollData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/polls/refresh`);
      setPollData({ polls: r.data.polls, trend: r.data.trend });
      setLastUpdated(r.data.last_updated);
      setSummary(r.data.summary || "");
    } catch (e) {
      console.error("Refresh failed:", e);
    } finally {
      setRefreshing(false);
    }
  };

  const latestPoll = pollData?.polls?.[0];
  const trend = pollData?.trend || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="poll-tracker-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            data-testid="polls-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Poll Tracker
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1
                className="text-4xl sm:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Live Polling Data
              </h1>
              <p
                className="text-base"
                style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}
              >
                National polling averages from major Australian pollsters.
              </p>
              {lastUpdated && (
                <p className="text-xs mt-1 flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.5)" }}>
                  <Clock className="w-3 h-3" />
                  AI refresh: {new Date(lastUpdated).toLocaleString("en-AU")}
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-polls-btn"
              className="flex items-center gap-2 px-4 py-2 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform mt-2 flex-shrink-0 disabled:opacity-60"
              style={{ backgroundColor: "#FFC82E", boxShadow: "2px 2px 0 #000" }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "AI Refresh"}
            </button>
          </div>
          {summary && (
            <div
              className="mt-4 p-3 border-2 border-white/20 text-sm italic"
              style={{ color: "rgba(255,255,255,0.8)", backgroundColor: "rgba(255,255,255,0.08)" }}
              data-testid="polls-ai-summary"
            >
              {summary}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Tabs */}
        <div className="flex gap-1 border-b-2 border-black mb-8">
          {[
            { id: "current", label: "Current Polls" },
            { id: "trend", label: "12-Month Trend" },
            { id: "tpp", label: "Two-Party Preferred" },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`poll-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className="px-5 py-2.5 font-bold text-sm border-2 transition-all"
              style={{
                borderBottomWidth: activeTab === tab.id ? "0" : "2px",
                borderColor: activeTab === tab.id ? "#000" : "transparent",
                backgroundColor: activeTab === tab.id ? "#FFC82E" : isDark ? "#1E293B" : "#F3F4F6",
                color: activeTab === tab.id ? "#000" : textSec,
                marginBottom: activeTab === tab.id ? "-2px" : "0",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="h-64 border-2 border-black animate-pulse" style={{ backgroundColor: surface }} />
        ) : (
          <>
            {/* Current Polls */}
            {activeTab === "current" && latestPoll && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  className="border-2 border-black p-5"
                  style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-black" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                      {latestPoll.pollster}
                    </h3>
                    <span className="text-xs font-bold px-2 py-1 border border-black" style={{ color: textSec }}>
                      {latestPoll.date}
                    </span>
                  </div>
                  <p className="text-xs mb-4" style={{ color: textSec }}>
                    Sample: {latestPoll.sample_size?.toLocaleString()} · {latestPoll.methodology}
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={latestPoll.primary_vote} layout="vertical" margin={{ left: 20 }}>
                      <XAxis type="number" domain={[0, 50]} tick={{ fontSize: 11, fill: textSec }} />
                      <YAxis type="category" dataKey="party" tick={{ fontSize: 11, fill: text }} width={70} />
                      <Tooltip
                        formatter={(v) => [`${v}%`, "Support"]}
                        contentStyle={{ backgroundColor: surface, border: "2px solid #000", borderRadius: 0 }}
                      />
                      <Bar dataKey="support" radius={0}>
                        {latestPoll.primary_vote.map((entry) => (
                          <Cell key={entry.party} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-3">
                  {pollData.polls.map((poll) => (
                    <div key={`${poll.pollster}-${poll.date}`} className="border-2 border-black p-4" style={{ backgroundColor: surface }}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-black text-sm" style={{ color: text }}>{poll.pollster}</span>
                        <span className="text-xs" style={{ color: textSec }}>{poll.date}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {poll.primary_vote.map((p) => (
                          <div key={p.party} className="flex items-center gap-1.5">
                            <div className="w-3 h-3 border border-black" style={{ backgroundColor: p.color }} />
                            <span className="text-xs font-bold" style={{ color: text }}>
                              {p.party}: {p.support}%
                            </span>
                            {p.change !== undefined && p.change !== 0 && (
                              <span className="text-xs" style={{ color: p.change > 0 ? "#10B981" : "#C04E3F" }}>
                                {p.change > 0 ? "+" : ""}{p.change}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs" style={{ color: textSec }}>
                    Click "AI Refresh" to get the latest GPT-4o analysis of current Australian polling.
                  </p>
                </div>
              </div>
            )}

            {/* Trend */}
            {activeTab === "trend" && (
              <div
                className="border-2 border-black p-6"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5" style={{ color: "#2E5948" }} />
                  <h3 className="font-black" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                    Primary Vote Trend (7 months)
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={trend} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: textSec }} />
                    <YAxis domain={[20, 45]} tick={{ fontSize: 11, fill: textSec }} unit="%" />
                    <Tooltip
                      formatter={(v) => [`${v}%`]}
                      contentStyle={{ backgroundColor: surface, border: "2px solid #000", borderRadius: 0 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: text }} />
                    <Line type="monotone" dataKey="ALP" stroke="#CC2529" strokeWidth={3} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Coalition" stroke="#1B4F9E" strokeWidth={3} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Greens" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Two-party preferred */}
            {activeTab === "tpp" && latestPoll?.two_pp && (
              <div
                className="border-2 border-black p-6 max-w-lg"
                style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
              >
                <h3 className="font-black mb-2" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
                  Two-Party Preferred — Latest ({latestPoll.pollster})
                </h3>
                <p className="text-sm mb-6" style={{ color: textSec }}>
                  How votes distribute between ALP and Coalition after preferences are counted.
                </p>
                {latestPoll.two_pp.map((p) => (
                  <div key={p.party} className="mb-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-black text-sm" style={{ color: text }}>{p.party}</span>
                      <span
                        className="font-black text-lg"
                        style={{ color: p.color, fontFamily: "Outfit, sans-serif" }}
                      >
                        {p.support}%
                      </span>
                    </div>
                    <div className="h-5 bg-gray-200 border-2 border-black">
                      <div className="h-full" style={{ width: `${p.support}%`, backgroundColor: p.color }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
